import { useCallback } from 'react'
import { cn } from '@/lib/utils'
import { Icon, type IconName } from '@/components/ui/icon'
import { SHADER_INPUT_LABELS, SHADER_INPUT_TYPES, type ShaderInputType } from '@/lib/canvas/shader/media/contract'

export const SHADER_INPUT_ICONS: Record<ShaderInputType, IconName> = {
  image: 'image',
  video: 'video',
  audio: 'music',
  text: 'type',
  '3d': 'box',
}

const CHIP_CLASS = 'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] leading-none'

interface ShaderInputBadgesProps {
  inputs: readonly ShaderInputType[]
  className?: string
}

/** The connected media a prompt will be written against, shown on the node face. */
export function ShaderInputBadges({ inputs, className }: ShaderInputBadgesProps) {
  if (inputs.length === 0) return null
  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)} aria-label="Connected inputs">
      <span className="text-[10px] text-muted-foreground">Uses</span>
      {SHADER_INPUT_TYPES.filter((type) => inputs.includes(type)).map((type) => (
        <span key={type} className={cn(CHIP_CLASS, 'border-border bg-muted/40 text-foreground')}>
          <Icon name={SHADER_INPUT_ICONS[type]} className="h-2.5 w-2.5" />
          {SHADER_INPUT_LABELS[type]}
        </span>
      ))}
    </div>
  )
}

interface ShaderInputTogglesProps {
  selected: readonly ShaderInputType[]
  /** Inputs with an edge: they keep their handle whatever is toggled here. */
  connected?: readonly ShaderInputType[]
  onChange: (next: ShaderInputType[]) => void
}

interface ToggleChipProps {
  type: ShaderInputType
  pressed: boolean
  wired: boolean
  onToggle: (type: ShaderInputType) => void
}

function ToggleChip({ type, pressed, wired, onToggle }: ToggleChipProps) {
  const handleClick = useCallback(() => onToggle(type), [onToggle, type])
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={handleClick}
      title={wired ? 'Connected — the handle stays while an edge uses it' : undefined}
      className={cn(
        CHIP_CLASS,
        'py-1 transition-colors',
        pressed ? 'border-primary/60 bg-primary/15 text-foreground' : 'border-border text-muted-foreground hover:text-foreground',
      )}
    >
      <Icon name={SHADER_INPUT_ICONS[type]} className="h-3 w-3" />
      {SHADER_INPUT_LABELS[type]}
      {wired && <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden />}
    </button>
  )
}

/** Which inputs a shader declares, so library or hand-written code can be given a handle. */
export function ShaderInputToggles({ selected, connected = [], onChange }: ShaderInputTogglesProps) {
  const handleToggle = useCallback((type: ShaderInputType) => {
    const next = selected.includes(type) ? selected.filter((t) => t !== type) : [...selected, type]
    onChange(SHADER_INPUT_TYPES.filter((t) => next.includes(t)))
  }, [selected, onChange])

  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Shader inputs">
      {SHADER_INPUT_TYPES.map((type) => (
        <ToggleChip key={type} type={type} pressed={selected.includes(type)} wired={connected.includes(type)} onToggle={handleToggle} />
      ))}
    </div>
  )
}
